import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Share2, Copy, Check, Loader2, Mail, Link2, Calendar, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface ExistingShare {
  id: string;
  shareToken: string;
  sharedAt: string;
  expiresAt?: string;
  viewCount: number;
  lastViewedAt?: string;
  status: 'active' | 'expired' | 'revoked';
  allowComments: boolean;
  allowApproval: boolean;
}

interface ShareSOWDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sowId: string;
  sowTitle: string;
  firmId: string;
  accessToken: string;
}

export function ShareSOWDialog({
  open,
  onOpenChange,
  sowId,
  sowTitle,
  firmId,
  accessToken,
}: ShareSOWDialogProps) {
  const [message, setMessage] = useState('');
  const [expirationDays, setExpirationDays] = useState(30);
  const [allowComments, setAllowComments] = useState(true);
  const [allowApproval, setAllowApproval] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  // Share management (ledger L06; step 5 of the simplification plan): the links already out for
  // this SOW, with their status, views and expiry, and a way to revoke one. Same routes the
  // share itself uses.
  const [shares, setShares] = useState<ExistingShare[] | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadShares = async () => {
    try {
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sows/${sowId}/shares`, { headers: { Authorization: `Bearer ${accessToken}` } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
      setShares(Array.isArray(body.shares) ? body.shares : []);
    } catch (e: any) {
      console.error('[ShareSOW] shares load failed:', e);
      setShares([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    setShares(null);
    void loadShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firmId, sowId]);

  const revoke = async (shareId: string) => {
    setRevoking(shareId);
    try {
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sows/${sowId}/shares/${encodeURIComponent(shareId)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
      toast.success('Link revoked');
      await loadShares();
    } catch (e: any) {
      toast.error(e?.message || 'Could not revoke the link');
    } finally {
      setRevoking(null);
    }
  };

  const handleShare = async () => {
    try {
      setSharing(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sows/${sowId}/share`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            message: message.trim() || undefined,
            expirationDays: expirationDays > 0 ? expirationDays : undefined,
            allowComments,
            allowApproval,
            sharedBy: 'current-user', // TODO: Get from context
            sharedByName: 'Consultant', // TODO: Get from context
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to share SOW');
      }

      const data = await response.json();
      setShareUrl(data.shareUrl);
      toast.success('SOW shared');
    } catch (error) {
      console.error('Error sharing SOW:', error);
      toast.error('Failed to share SOW');
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleEmailShare = () => {
    if (!shareUrl) return;

    const subject = encodeURIComponent(`Statement of Work: ${sowTitle}`);
    const body = encodeURIComponent(
      `Hi,\n\nPlease review the Statement of Work for ${sowTitle}:\n\n${shareUrl}\n\n${
        message ? `Message: ${message}\n\n` : ''
      }Best regards`
    );

    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleReset = () => {
    setMessage('');
    setExpirationDays(30);
    setAllowComments(true);
    setAllowApproval(true);
    setShareUrl('');
    setCopied(false);
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) {
          handleReset();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5 text-blue-600" />
            Share Statement of Work
          </DialogTitle>
          <DialogDescription>
            Generate a shareable link for client review and approval
          </DialogDescription>
        </DialogHeader>

        {!shareUrl ? (
          <div className="space-y-4 py-4">
            {/* Message */}
            <div>
              <Label htmlFor="message">Personal Message (Optional)</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a personal note for your client..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                This message will be displayed at the top of the SOW
              </p>
            </div>

            {/* Expiration */}
            <div>
              <Label htmlFor="expiration">Link Expiration (Days)</Label>
              <Input
                id="expiration"
                type="number"
                min={1}
                max={365}
                value={expirationDays}
                onChange={(e) => setExpirationDays(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Link will expire after {expirationDays} days. Set to 0 for no expiration.
              </p>
            </div>

            {/* Permissions */}
            <div className="space-y-3 border rounded-lg p-4">
              <h4 className="font-semibold text-sm">Client Permissions</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="allow-comments" className="font-normal">
                    Allow Comments
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Client can ask questions and leave comments
                  </p>
                </div>
                <Switch
                  id="allow-comments"
                  checked={allowComments}
                  onCheckedChange={setAllowComments}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="allow-approval" className="font-normal">
                    Allow Approval
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Client can approve, request changes, or reject
                  </p>
                </div>
                <Switch
                  id="allow-approval"
                  checked={allowApproval}
                  onCheckedChange={setAllowApproval}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleShare}
                disabled={sharing}
              >
                {sharing ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Generating Link...
                  </>
                ) : (
                  <>
                    <Share2 className="size-4 mr-2" />
                    Generate Share Link
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Check className="size-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">
                    Share link generated.
                  </h4>
                  <p className="text-sm text-green-700">
                    Send this link to your client for review and approval.
                  </p>
                </div>
              </div>
            </div>

            {/* Share Link */}
            <div>
              <Label>Shareable Link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={shareUrl}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={handleCopy}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <>
                      <Check className="size-4 mr-2" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="size-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Link Details */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Calendar className="size-4" />
                  Expires
                </div>
                <p className="font-medium">
                  {expirationDays > 0
                    ? `In ${expirationDays} days`
                    : 'Never'}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Link2 className="size-4" />
                  Permissions
                </div>
                <div className="flex gap-1">
                  {allowComments && (
                    <Badge variant="secondary" className="text-xs">
                      Comments
                    </Badge>
                  )}
                  {allowApproval && (
                    <Badge variant="secondary" className="text-xs">
                      Approval
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-2">
              <Label>Quick Actions</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleEmailShare}
                  className="flex-1"
                >
                  <Mail className="size-4 mr-2" />
                  Email Link
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCopy}
                  className="flex-1"
                >
                  <Copy className="size-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end pt-4">
              <Button onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Existing links */}
        <div className="border-t pt-4 mt-2" data-testid="existing-shares">
          <div className="flex items-center justify-between mb-2">
            <Label>Links already shared</Label>
            {shares && shares.length > 0 && <span className="text-xs text-muted-foreground">{shares.filter((x) => x.status === 'active').length} active of {shares.length}</span>}
          </div>
          {shares === null ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> Loading…</p>
          ) : shares.length === 0 ? (
            <p className="text-sm text-muted-foreground">No links shared yet.</p>
          ) : (
            <ul className="space-y-2">
              {shares.map((x) => (
                <li key={x.id} className="flex items-center justify-between gap-3 text-sm rounded-md border px-3 py-2" data-testid="existing-share">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={x.status === 'active' ? 'default' : 'secondary'} className="text-xs capitalize">{x.status}</Badge>
                      <span className="text-muted-foreground">shared {new Date(x.sharedAt).toLocaleDateString()}</span>
                      <span className="text-muted-foreground">· {x.viewCount} view{x.viewCount === 1 ? '' : 's'}</span>
                      {x.expiresAt && <span className="text-muted-foreground">· expires {new Date(x.expiresAt).toLocaleDateString()}</span>}
                    </div>
                    <div className="flex gap-1 mt-1">
                      {x.allowComments && <Badge variant="outline" className="text-xs">Comments</Badge>}
                      {x.allowApproval && <Badge variant="outline" className="text-xs">Approval</Badge>}
                    </div>
                  </div>
                  {x.status === 'active' && (
                    <Button variant="ghost" size="sm" onClick={() => revoke(x.id)} disabled={revoking === x.id} aria-label="Revoke link">
                      {revoking === x.id ? <Loader2 className="size-4 animate-spin" /> : <Ban className="size-4 text-red-600" />}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}